import * as React from 'react';
import { useState } from 'react';

import { isAxiosError } from '~api/isAxiosError';

import { sendTeacherAmplitudeEvent } from '~analytics/amplitude';
import { EventTypes } from '~analytics/types';
import { translate } from '~l10n/client';
import type { SmallButtonProps } from '~shared/components/buttons';
import { SmallButton } from '~shared/components/buttons';
import { IconAI } from '~shared/components/icons/standard';
import { useTeacherApplicationContext } from '~shared/context/TeacherApplicationContext';
import { useStartPoll } from '~shared/polling';
import { useGenerateInsightsMutation } from '~teacher/hook';
import { useAiErrorHandler } from '~teacher/hook/useAiErrorHandler';

interface InsightsGenerateInsightsButtonProps {
  isLoading: boolean;
  onClickCapture: SmallButtonProps['onClickCapture'];
}

export function InsightsGenerateInsightsButton({
  isLoading,
  onClickCapture,
}: InsightsGenerateInsightsButtonProps) {
  return (
    <SmallButton
      noWrap
      isLoading={isLoading}
      onClickCapture={onClickCapture}
      xPadding={0}
    >
      <div className="flex items-center pl-2 pr-4">
        <IconAI />
        {translate('Analyze Report')}
      </div>
    </SmallButton>
  );
}

interface UseInsightsGenerateInsightsButtonParams {
  activityInstanceId: number;
  averageScoreRate?: number | null;
}

interface UseInsightsGenerateInsightsButtonResult {
  isLoading: boolean;
  onClickCapture: SmallButtonProps['onClickCapture'];
  showNoInsightsDialog: boolean;
  setShowNoInsightsDialog: (show: boolean) => void;
}

export function useInsightsGenerateInsightsButton({
  activityInstanceId,
  averageScoreRate,
}: UseInsightsGenerateInsightsButtonParams): UseInsightsGenerateInsightsButtonResult {
  const applicationContext = useTeacherApplicationContext();
  const handleAiError = useAiErrorHandler({
    featureTag: 'ai-generate-insights',
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [showNoInsightsDialog, setShowNoInsightsDialog] = useState(false);
  const postGenerateInsightsMutation = useGenerateInsightsMutation();
  const { startPoll } = useStartPoll();

  const onClickCapture: SmallButtonProps['onClickCapture'] = async (event) => {
    event.stopPropagation();

    // Check if avg score is 100%
    if (averageScoreRate === 100) {
      setShowNoInsightsDialog(true);
      return;
    } else {
      setShowNoInsightsDialog(false);
    }

    setIsGenerating(true);
    sendTeacherAmplitudeEvent(EventTypes.ClickGenerateAIInsights);

    try {
      await postGenerateInsightsMutation.mutateAsync({
        activityInstanceId,
      });

      // Start polling for insights using the new generic polling system
      startPoll({
        id: `insights-${activityInstanceId}`,
        type: 'ai-insights',
        metadata: {
          activityInstanceId,
          applicationContext,
        },
        preserveHistory: false,
      });
    } catch (error) {
      if (
        isAxiosError(error) &&
        error.response?.status === 400 &&
        (error.response?.data as any)?.code === 'insufficient_data_error'
      ) {
        // insufficient_data_error is handled by useGenerateInsightsMutation,
        // which sets GenerationStatus.InsufficientData in the query cache.
        // The InsightsInsufficientData component renders the appropriate UI.
        return;
      } else {
        await handleAiError(error);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    isLoading: isGenerating,
    onClickCapture: onClickCapture,
    showNoInsightsDialog,
    setShowNoInsightsDialog,
  };
}
